import React, { useEffect, useState } from "react";
import { message, Space, Switch, Modal, Form, Input, InputNumber } from "antd";
import Service from "../../services";

const ExternalAccountSwitch = ({ tradeAccountStatus, setTradeAccountStatus }) => {
  const [isModalVisible, setIsModalVisible] = useState(false); // Add state for the modal visibility
  const [switchChecked, setSwitchChecked] = useState(false); // Default state of switch
  const [form] = Form.useForm(); // Initialize the form

  useEffect(() => {
    setSwitchChecked(tradeAccountStatus?.status?.toLowerCase().startsWith("external"));
  }, [tradeAccountStatus]);

  const updateTradeAccountStatusAndConfig = async (configs, status) => {
    try {
      // Convert configs to the required stringifyConfigs format
      const stringifyConfigs = JSON.stringify(JSON.stringify(configs));
      // Update backend data
      await Service.updateTradeAccountStatus({
        exchange: tradeAccountStatus.exchange,
        accountSymbol: tradeAccountStatus.accountSymbol,
        configs: stringifyConfigs,
        status,
      });
      // Update local state
      setTradeAccountStatus((prevState) => ({
        ...prevState,
        status,
        configs: stringifyConfigs,
      }));
      message.success(`Trade Account Status Updated to ${status}`);
    } catch (error) {
      console.error("Failed to update trade account status:", error);
      message.error("Failed to update trade account status.");
    }
  };

  const handleSwitchChange = async (checked) => {
    setIsModalVisible(true);
    const currentConfigs = JSON.parse(JSON.parse(tradeAccountStatus.configs));
    // Pre-fill the form with data when modal opens
    const circuitBreakerInitialBalance = currentConfigs.circuitBreakerInitialBalance;
    const thresholds = [
      currentConfigs.circuitBreakerDailyThreshold,
      currentConfigs.circuitBreakerWeeklyThreshold,
      currentConfigs.circuitBreakerMonthlyThreshold,
    ].map((value) => (value === undefined ? undefined : Number(value)));
    const validThresholds = thresholds.filter((v) => v !== undefined);
    const minThreshold = validThresholds.length > 0 ? Math.min(...validThresholds) : 10; // Set minThreshold to 10 if the minimum value is Infinity
    form.setFieldsValue({
      statusSuffix: switchChecked ? tradeAccountStatus.status : "",
      initialBalance: circuitBreakerInitialBalance || "",
      maxDropdown: minThreshold, // Default value
    });
  };

  const handleConfirm = async () => {
    const currentConfigs = JSON.parse(JSON.parse(tradeAccountStatus.configs));
    let updatedStatus = switchChecked ? "positionClosed" : `external: ${form.getFieldValue("statusSuffix")}`;
    currentConfigs.enableCircuitBreaker = switchChecked ? "false" : "true";
    if (!switchChecked) {
      const values = await form.validateFields();
      currentConfigs.circuitBreakerDailyThreshold = values.maxDropdown;
      currentConfigs.circuitBreakerWeeklyThreshold = values.maxDropdown;
      currentConfigs.circuitBreakerMonthlyThreshold = values.maxDropdown;
      currentConfigs.circuitBreakerInitialBalance = values.initialBalance;
    }
    await updateTradeAccountStatusAndConfig(currentConfigs, updatedStatus);
    setIsModalVisible(false);
    setSwitchChecked(!switchChecked); // Toggle the switch based on the current action
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <>
      <Space>
        <span>Set to External:</span>
        <Switch checkedChildren="Yes" unCheckedChildren="No" checked={switchChecked} onChange={handleSwitchChange} />
      </Space>
      <Modal
        title={switchChecked ? "Set Account back to Internal" : "Set Account to External"}
        open={isModalVisible}
        onOk={handleConfirm}
        onCancel={handleCancel}
        okText="Confirm"
        cancelText="Cancel"
      >
        <Form form={form} layout="vertical" initialValues={{ maxDropdown: 10 }}>
          <Form.Item name="statusSuffix" label="Status Suffix" rules={[{ required: true, message: "Please input the status suffix!" }]}>
            <Input addonBefore="external: " disabled={switchChecked} />
          </Form.Item>
          <Form.Item name="initialBalance" label="Initial Balance">
            <Input disabled={switchChecked} />
          </Form.Item>
          <Form.Item label="Max Dropdown (%)" name="maxDropdown">
            <InputNumber
              min={0}
              max={100}
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace("%", "")}
              style={{ width: "100%" }}
              disabled={switchChecked}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
export default ExternalAccountSwitch;
